<template>
  <div
    class="location-search-wrapper"
    :class="{ 'is-search-page': isSearchPage }"
  >
    <div class="search-input-wrapper">
      <b-input-group>
        <b-form-input
          id="locationSearchInput"
          ref="searchInput"
          class="search-input-component"
          :class="{ 'using-current-location': useCurrentLocation }"
          v-model="searchValue"
          :disabled="disabled"
          placeholder="Search by town or postcode"
          @input="debounceSearchLocations()"
          @keyup.enter="handleEnterEvent"
          @focus="searchInputFocused()"
        />

        <div id="search-results-dropdown" v-if="searchResults">
          <ul
            class="dropdown-menu"
            :class="[showResultsDropDown ? 'show' : 'hide']"
          >
            <li
              v-for="(result, resultIndex) in searchResults"
              :key="resultIndex"
            >
              <div class="dropdown-item" @click="selectOption(result)">
                {{ result.address_components[0] && result.address_components[0].long_name ? result.address_components[0].long_name : '' }}
                {{ result.address_components[1] && result.address_components[1].long_name ? `, ${result.address_components[1].long_name}` : '' }}
              </div>
            </li>
          </ul>
        </div>

        <b-input-group-append>
          <b-button
            class="your-location d-inline-flex"
            variant="secondary"
            :pressed="useCurrentLocation"
            @click="toggleUseCurrentLocation()"
            v-b-tooltip.hover.bottom="{ customClass: 'ngs-dark-tooltip' }"
            title="Your location"
          >
            <font-awesome-icon :icon="['far', 'location']" class="align-self-center" />
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'

export default {
  name: 'LocationSearch',

  props: {
    isSearchPage: {
      type: Boolean,
      default: false,
    },
    deferSearch: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: undefined,
    },
  },

  computed: {
    ...mapGetters('garden', [
      'searchTerm',
      'searchResults',
      'locationSearchType',
    ]),

    ...mapGetters('location', [
      'useCurrentLocation',
    ]),

    isControlled() {
      return this.value !== undefined
    },

    searchValue: {
      get() {
        if (this.isControlled) {
          return this.value != null ? this.value : ''
        }
        return this.searchTerm || ''
      },
      set(value) {
        this.$emit('input', value)
        if (!this.isControlled) {
          this.$store.commit('garden/updateSearchTerm', value)
        }
      },
    },

    showResultsDropDown() {
      return !!(this.searchResults && this.searchResults.length)
    },
  },

  mounted() {
    this.updateSearchType(this.locationSearchType)
  },

  methods: {
    ...mapActions('garden', [
      'updateSearchType',
      'search',
      'searchLocations',
    ]),

    ...mapActions('location', [
      'setUseCurrentLocation',
      'getSelectedLocationBoundsAndUpdate',
    ]),

    handleEnterEvent() {
      this.search()
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur()
      }
    },

    debounceSearchLocations: debounce(function () {
      this.searchLocations(this.searchValue)
    }, 300),

    toggleUseCurrentLocation() {
      const newState = !this.useCurrentLocation
      this.searchValue = ''
      this.$emit('clear-inputs')
      this.setUseCurrentLocation(newState)
      if (newState) {
        this.$emit('use-current-location')
      }
    },

    selectOption(result) {
      if (this.deferSearch) {
        const term = result.address_components.length > 1
          ? `${result.address_components[0].long_name}, ${result.address_components[1].long_name}`
          : result.address_components[0].long_name
        this.searchValue = term
        this.$store.dispatch('garden/clearSearchResults')
        this.$emit('location-selected', result)
      } else {
        this.getSelectedLocationBoundsAndUpdate({
          location: result,
          keepFilters: true,
        })
      }
    },

    searchInputFocused() {
      if (this.useCurrentLocation) {
        this.setUseCurrentLocation(false)
      }
      this.updateSearchType(this.locationSearchType)
    },
  },
}
</script>

<style lang="scss">
.location-search-wrapper {
  flex: 1;

  &.is-search-page {
    .search-input-wrapper {
      #search-results-dropdown {
        left: 0;
        top: 40px;
      }

      .input-group {
        border: 1px solid rgba($color-grey-dark, 0.2);
        border-radius: 4px;
        overflow: visible;
        min-height: 40px;

        .form-control {
          border: none;
          min-height: 40px;
          padding: 10px 12px;
          font-family: "Gill Sans", "Inter", sans-serif;
          font-size: 18px;
          color: $color-grey-dark;

          &::placeholder {
            color: $color-grey-medium;
          }

          &:focus {
            box-shadow: none;
            border: none;
          }
        }

        .input-group-append .your-location {
          min-height: 40px;
          padding: 0 12px;
          border: none;
          border-left: 1px solid rgba($color-grey-dark, 0.2);
          border-radius: 0;
          background-color: #fff;
          color: $color-grey-dark;

          &:hover,
          &:focus {
            background-color: rgba(0, 0, 0, 0.04);
            border-left-color: rgba($color-grey-dark, 0.2);
            box-shadow: none;
          }

          &.active {
            background-color: rgba($primary, 0.15);
            border-left-color: rgba($color-grey-dark, 0.2);
          }
        }
      }
    }
  }

  .search-input-wrapper {
    width: 100%;

    .input-group {
      justify-content: center;
      position: relative;
      border: 1px solid $color-grey-dark;
      border-radius: 5px;
    }

    #locationSearchInput {
      &:focus {
        box-shadow: none;
        border: 1px solid #ced4da;
      }
      &.using-current-location {
        border-left: none;
      }
    }

    .input-group-append .your-location {
      &:hover {
        z-index: 50;
        box-shadow: none;
        border: 1px solid $color-grey-dark;
      }

      &:focus {
        border-right: 1px solid $color-grey-dark;
        background-color: #ececec;
      }

      &.active {
        border: 1px solid $primary;
        border-left: 1px solid $color-grey-dark;
        background-color: $primary;
      }
    }
  }

  #search-results-dropdown {
    position: absolute;
    left: 0;
    top: 37px;

    .dropdown-menu {
      -webkit-box-shadow: 0px 5px 5px -3px #00000033, 0px 8px 10px 1px #00000024, 0px 3px 14px 2px #0000001f;
      -moz-box-shadow: 0px 5px 5px -3px #00000033, 0px 8px 10px 1px #00000024, 0px 3px 14px 2px #0000001f;
      box-shadow: 0px 5px 5px -3px #00000033, 0px 8px 10px 1px #00000024, 0px 3px 14px 2px #ffffff00;
      position: absolute;
      z-index: 1000;
      display: none;
      min-width: 10rem;
      padding: 0;
      margin: 0;
      font-size: 1rem;
      color: $color-grey-medium;
      text-align: left;
      list-style: none;
      background-color: #fff;
      background-clip: padding-box;
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-top: none;
      border-radius: 0.25rem;
      border-top-right-radius: 0;
      border-top-left-radius: 0;

      &.show {
        display: block;
        position: absolute;
        transform: translate3d(0px, 0px, 0px);
        top: 0;
        left: 0;
        will-change: transform;
      }

      li .dropdown-item {
        cursor: pointer;
        display: flex;
        align-items: center;
        font-size: 0.875rem;
        font-weight: 400;
        color: $color-grey-medium;
        width: 100%;
        padding: 0.25rem 1rem;
        clear: both;
        text-align: inherit;
        text-decoration: none;
        white-space: nowrap;
        background-color: transparent;
        border: 0;

        &:hover {
          color: #1e2125;
          background-color: #e9ecef;
        }
      }
    }
  }
}
</style>
